// src/Pages/Admin/EstabilishmentUserManagement/index.tsx

import React, { useEffect, useState } from "react";
import {
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { planoPorVeiculacao } from "../../../Services/Plan";
import {
  getEstabilishmentUsers,
  moderateEstabilishmentUser,
  reviewUserEstabilishmentDocuments,
  deleteEstabilishmentUser,
  postStripeCheckout,
  getPlans,
} from "../../../Services/Stripe";
import {
  IEstabilishmentUser,
  IRestGetEstabilishmentUsers,
  IRestStripeCheckout,
  IStripePlan,
  IRestGetStripePlans,
} from "../../../Services/Interfaces";

// ─── Inline SVG icons ──────────────────────────────────────────────────────────

const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#aaa"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const FilterIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const ExportIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const ActionIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const ChevronLeft = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

// ─── Pagination helper ─────────────────────────────────────────────────────────

const getPaginationPages = (
  current: number,
  total: number,
): (number | "...")[] => {
  if (total <= 6) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, "...", total - 1, total];
  if (current >= total - 2) return [1, 2, "...", total - 2, total - 1, total];
  return [1, "...", current - 1, current, current + 1, "...", total];
};

// ─── Component ─────────────────────────────────────────────────────────────────

type Tab = "aplicativo" | "administrativos";

export const EstabilishmentUserManagement = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<Tab>("aplicativo");
  const [users, setUsers] = useState<IEstabilishmentUser[]>([]);
  const [plans, setPlans] = useState<IStripePlan[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  // Toggle dialog
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [pendingUser, setPendingUser] = useState<IEstabilishmentUser | null>(
    null,
  );
  const [toggleLoading, setToggleLoading] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteUser,       setDeleteUser]       = useState<IEstabilishmentUser | null>(null);
  const [deleteLoading,    setDeleteLoading]    = useState(false);

  // ── Análise de documentos (docs do estabelecimento + selfie) ──────────────
  // Aprovar aqui libera Banner e Push do plano Connection.
  const [docUser,     setDocUser]     = useState<IEstabilishmentUser | null>(null);
  const [docAction,   setDocAction]   = useState<"approved" | "rejected">("approved");
  const [docReason,   setDocReason]   = useState("");
  const [docLoading,  setDocLoading]  = useState(false);

  // ── Análise por unidade ───────────────────────────────────────────────────
  // Necessário quando a conta já está aprovada e o usuário cadastra uma
  // unidade nova: o botão do nível da conta não aparece nesse caso.
  const [unitsUser,    setUnitsUser]    = useState<IEstabilishmentUser | null>(null);
  const [unitLoading,  setUnitLoading]  = useState("");
  const [unitReason,   setUnitReason]   = useState<{ id: string; text: string } | null>(null);

  const handleUnitReview = (
    ueId: string,
    status: "approved" | "rejected",
    reason?: string,
  ) => {
    if (status === "rejected" && (!reason || reason.trim().length < 5)) {
      toast.warning("Descreva o que precisa ser ajustado.");
      return;
    }

    setUnitLoading(ueId);
    reviewUserEstabilishmentDocuments(ueId, status, reason?.trim())
      .then(() => {
        toast.success(
          status === "approved"
            ? "Unidade aprovada."
            : "Recusa registrada. O responsável foi avisado por e-mail.",
        );
        setUnitReason(null);
        setUnitsUser(null);
        fetchUsers();
      })
      .catch(() => toast.error("Não foi possível registrar a análise."))
      .finally(() => setUnitLoading(""));
  };

  const openDocDialog = (user: IEstabilishmentUser, action: "approved" | "rejected") => {
    setDocUser(user);
    setDocAction(action);
    setDocReason("");
  };

  const handleDocConfirm = () => {
    if (!docUser) return;
    if (docAction === "rejected" && docReason.trim().length < 5) {
      toast.warning("Descreva o que precisa ser ajustado.");
      return;
    }

    // O endpoint é por vínculo (user_estabilishment.id), não por usuário —
    // por isso usamos a 1ª unidade do usuário, não docUser.id diretamente.
    const unidades = (docUser as any).userEstabilishments ?? [];
    const unidadeId = unidades[0]?.id;

    if (!unidadeId) {
      toast.error("Não foi possível identificar o estabelecimento deste usuário.");
      return;
    }

    setDocLoading(true);
    reviewUserEstabilishmentDocuments(
      unidadeId,
      docAction,
      docAction === "rejected" ? docReason.trim() : undefined,
    )
      .then(() => {
        toast.success(
          docAction === "approved"
            ? "Documentos aprovados!"
            : "Recusa registrada. O usuário foi avisado por e-mail.",
        );
        setDocUser(null);
        fetchUsers();
      })
      .catch(() => toast.error("Não foi possível registrar a análise."))
      .finally(() => setDocLoading(false));
  };

  // Checkout dialog
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IEstabilishmentUser | null>(
    null,
  );
  const [selectedPlan, setSelectedPlan] = useState("");
  const [selectedEstabilishment, setSelectedEstabilishment] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // ── Fetch ────────────────────────────────────────────────────────────────────

  const fetchUsers = () => {
    setIsLoading(true);
    getEstabilishmentUsers(
      (currentPage - 1) * PAGE_SIZE,
      PAGE_SIZE,
      appliedSearch,
      appliedSearch,
    )
      .then((res) => {
        const { data } = res as IRestGetEstabilishmentUsers;
        setTotalCount(data.pagination.total);
        setUsers(data.data);
      })
      .catch(() => toast.error("Erro ao carregar usuários."))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, appliedSearch]);

  useEffect(() => {
    getPlans().then((res) => {
      const { data } = res as IRestGetStripePlans;
      setPlans(data.data);
    });
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleFilter = () => {
    setCurrentPage(1);
    setAppliedSearch(search);
  };

  const handleClear = () => {
    setSearch("");
    setAppliedSearch("");
    setCurrentPage(1);
  };

  const handleToggleClick = (user: IEstabilishmentUser) => {
    setPendingUser(user);
    setToggleDialogOpen(true);
  };

  const handleToggleConfirm = () => {
    if (!pendingUser) return;
    setToggleLoading(true);
    moderateEstabilishmentUser(pendingUser.id, !pendingUser.isModered)
      .then(() => {
        toast.success(
          `Usuário ${!pendingUser.isModered ? "aprovado" : "reprovado"}!`,
        );
        setToggleDialogOpen(false);
        fetchUsers();
      })
      .catch(() => toast.error("Erro ao moderar usuário."))
      .finally(() => setToggleLoading(false));
  };

  const handleDeleteClick = (user: IEstabilishmentUser) => {
    setDeleteUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!deleteUser) return;
    setDeleteLoading(true);
    deleteEstabilishmentUser(deleteUser.id)
      .then(() => {
        toast.success("Usuário excluído com sucesso.");
        setDeleteDialogOpen(false);
        fetchUsers();
      })
      .catch(() => toast.error("Erro ao excluir usuário."))
      .finally(() => setDeleteLoading(false));
  };

  const handleCheckout = () => {
    if (!selectedPlan || !selectedEstabilishment) {
      toast.warning("Selecione um plano e um estabelecimento.");
      return;
    }
    setCheckoutLoading(true);
    postStripeCheckout(selectedPlan, selectedEstabilishment)
      .then((res) => {
        const { data } = res as IRestStripeCheckout;
        setCheckoutOpen(false);
        window.open(data.data.checkout_url, "_blank");
      })
      .catch(() => toast.error("Erro ao iniciar checkout."))
      .finally(() => setCheckoutLoading(false));
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // ── Inline styles ─────────────────────────────────────────────────────────────

  const s: Record<string, React.CSSProperties> = {
    page: {
      background: "#fff",
      borderRadius: 16,
      padding: "28px 32px",
      minHeight: "100%",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 24,
    },
    title: { fontSize: 28, fontWeight: 700, color: "#111", margin: 0 },
    tabs: { display: "flex", gap: 8, marginBottom: 20 },
    toolbar: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginBottom: 24,
    },
    searchWrap: {
      flex: 1,
      maxWidth: 400,
      display: "flex",
      alignItems: "center",
      gap: 8,
      border: "1.5px solid #e8e8e8",
      borderRadius: 10,
      padding: "0 14px",
      height: 42,
      background: "#fff",
    },
    searchInput: {
      border: "none",
      outline: "none",
      fontSize: 14,
      color: "#333",
      flex: 1,
      background: "transparent",
    },
    btnTeal: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      height: 42,
      padding: "0 18px",
      backgroundColor: "#00b6bc",
      color: "#fff",
      border: "none",
      borderRadius: 10,
      fontWeight: 600,
      fontSize: 14,
      cursor: "pointer",
    },
    btnOutline: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      height: 42,
      padding: "0 18px",
      backgroundColor: "#fff",
      color: "#444",
      border: "1.5px solid #ddd",
      borderRadius: 10,
      fontWeight: 600,
      fontSize: 14,
      cursor: "pointer",
    },
    btnRight: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      height: 42,
      padding: "0 18px",
      backgroundColor: "#00b6bc",
      color: "#fff",
      border: "none",
      borderRadius: 10,
      fontWeight: 600,
      fontSize: 14,
      cursor: "pointer",
      marginLeft: "auto",
    },
    table: { width: "100%", borderCollapse: "collapse" },
    th: {
      textAlign: "left",
      padding: "12px 16px",
      fontSize: 13,
      fontWeight: 600,
      color: "#888",
      borderBottom: "1.5px solid #f0f0f0",
      whiteSpace: "nowrap",
    },
    td: {
      padding: "14px 16px",
      fontSize: 14,
      color: "#222",
      borderBottom: "1px solid #f5f5f5",
      verticalAlign: "middle",
    },
    tdMuted: {
      padding: "14px 16px",
      fontSize: 14,
      color: "#999",
      borderBottom: "1px solid #f5f5f5",
      verticalAlign: "middle",
    },
    statusWrap: { display: "flex", alignItems: "center", gap: 6 },
    actionBtn: {
      width: 36,
      height: 36,
      borderRadius: 8,
      border: "none",
      backgroundColor: "#00b6bc",
      color: "#fff",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    emptyCell: {
      textAlign: "center",
      padding: "48px 16px",
      color: "#bbb",
      fontSize: 14,
    },
    pagination: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 4,
      marginTop: 24,
    },
    dialogBtn: {
      padding: "10px 24px",
      borderRadius: 10,
      border: "1.5px solid #ddd",
      background: "#fff",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: 14,
      color: "#555",
    },
    dialogBtnPrimary: {
      padding: "10px 24px",
      borderRadius: 10,
      border: "none",
      background: "#00b6bc",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: 14,
      color: "#fff",
    },
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: "8px 22px",
    borderRadius: 999,
    border: "none",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
    transition: "all 0.15s",
    backgroundColor: active ? "#00b6bc" : "transparent",
    color: active ? "#fff" : "#555",
    outline: active ? "none" : "1.5px solid #ddd",
  });

  const pageBtn = (
    active: boolean,
    disabled?: boolean,
  ): React.CSSProperties => ({
    minWidth: 34,
    height: 34,
    borderRadius: "50%",
    border: "none",
    backgroundColor: active ? "#00b6bc" : "transparent",
    color: active ? "#fff" : disabled ? "#ccc" : "#555",
    fontWeight: active ? 700 : 500,
    fontSize: 14,
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  });

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={s.page}>
      {/* Title */}
      <div style={s.header}>
        <h1 style={s.title}>Usuários</h1>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        <button
          style={tabStyle(activeTab === "aplicativo")}
          onClick={() => setActiveTab("aplicativo")}
        >
          Aplicativo
        </button>
        <button
          style={tabStyle(activeTab === "administrativos")}
          onClick={() => setActiveTab("administrativos")}
        >
          Administrativos
        </button>
      </div>

      {/* Toolbar */}
      <div style={s.toolbar}>
        <div style={s.searchWrap}>
          <SearchIcon />
          <input
            style={s.searchInput}
            placeholder="Pesquisar por nome ou e-mail"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleFilter()}
          />
        </div>

        <button style={s.btnTeal} onClick={handleFilter}>
          <FilterIcon /> Filtrar
        </button>

        <button style={s.btnOutline} onClick={handleClear}>
          <ExportIcon /> Exportar relatório
        </button>
      </div>

      {/* Table */}
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Nome</th>
            <th style={s.th}>E-mail</th>
            <th style={s.th}>País</th>
            <th style={s.th}>Cidade</th>
            <th style={s.th}>Status</th>
            <th style={s.th}>Documentos</th>
            <th style={s.th}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={6} style={s.emptyCell}>
                <CircularProgress size={24} style={{ color: "#00b6bc" }} />
              </td>
            </tr>
          ) : users.length === 0 ? (
            <tr>
              <td colSpan={6} style={s.emptyCell}>
                Nenhum usuário encontrado.
              </td>
            </tr>
          ) : (
            users.map((user) => (
              <tr
                key={user.id}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#fafafa")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <td style={s.td}>{user.profile?.fullName || "—"}</td>
                <td style={s.td}>{user.email}</td>
                <td style={s.tdMuted}>
                  {(user as any)?.profile?.address?.country?.name || "—"}
                </td>
                <td style={s.tdMuted}>
                  {(user as any)?.profile?.address?.city?.name || "—"}
                </td>
                <td style={s.td}>
                  <div style={s.statusWrap}>
                    <Switch
                      size="small"
                      checked={!!user.isModered}
                      onChange={() => handleToggleClick(user)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#00b6bc",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { backgroundColor: "#00b6bc" },
                      }}
                    />
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: user.isModered ? "#00b6bc" : "#999",
                      }}
                    >
                      {user.isModered ? "Ativo" : "Bloqueado"}
                    </span>
                  </div>
                </td>

                {/* Análise dos documentos — aprovar libera Banner e Push */}
                <td style={s.td}>
                  {(() => {
                    const unidades = (user as any).userEstabilishments ?? [];
                    // unidades ainda sem análise, mesmo em conta já aprovada
                    const pendentes = unidades.filter(
                      (ue: any) =>
                        ue.documentsStatus === "pending" ||
                        ue.documentsStatus === "not_sent",
                    );

                    // Aviso que faltava: conta aprovada com unidade nova na
                    // fila. Antes só aparecia "Aprovado" e a segunda unidade
                    // passava despercebida.
                    const avisoUnidades =
                      pendentes.length > 0 && unidades.length > 1 ? (
                        <button
                          onClick={() => setUnitsUser(user)}
                          style={{
                            marginTop: 6,
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#b45309",
                            background: "#fff7e6",
                            border: "1px solid #fde68a",
                            borderRadius: 6,
                            padding: "4px 10px",
                            display: "inline-block",
                            cursor: "pointer",
                          }}
                          title={pendentes
                            .map((ue: any) => ue.estabilishment?.name)
                            .filter(Boolean)
                            .join(" · ")}
                        >
                          +{pendentes.length} unidade(s) a analisar →
                        </button>
                      ) : null;

                    const st = (user as any).documentsStatus ?? "not_sent";

                    if (st === "approved") {
                      return (
                        <div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1a7a4a" }}>
                            Aprovado
                          </span>
                          {unidades.length > 1 && (
                            <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
                              {unidades.length} unidades
                            </div>
                          )}
                          {avisoUnidades}
                        </div>
                      );
                    }

                    if (st === "not_sent") {
                      return (
                        <span style={{ fontSize: 13, color: "#aaa" }}>
                          Não enviados
                        </span>
                      );
                    }

                    // pending ou rejected: permite analisar
                    return (
                      <div>
                        <div style={{
                          fontSize: 12, fontWeight: 700, marginBottom: 6,
                          color: st === "rejected" ? "#b91c1c" : "#b45309",
                        }}>
                          {st === "rejected" ? "Recusado" : "Em análise"}
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            onClick={() => openDocDialog(user, "approved")}
                            style={{
                              padding: "4px 10px", borderRadius: 7, border: "none",
                              background: "#00b6bc", color: "#fff",
                              fontSize: 11, fontWeight: 700, cursor: "pointer",
                            }}
                          >
                            Aprovar
                          </button>
                          <button
                            onClick={() => openDocDialog(user, "rejected")}
                            style={{
                              padding: "4px 10px", borderRadius: 7,
                              border: "1.5px solid #ec4899", background: "#fff",
                              color: "#ec4899", fontSize: 11, fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            Recusar
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </td>

                <td style={s.td}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <button
                      style={s.actionBtn}
                      title="Ver detalhes"
                      onClick={() =>
                        navigate(`/EstabilishmentUserManagement/${user.id}`)
                      }
                    >
                      <ActionIcon />
                    </button>
                    <button
                      style={{
                        width: 32, height: 32, borderRadius: 8,
                        border: "1.5px solid #ec4899", background: "#fdf2f8",
                        color: "#ec4899", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                      title="Excluir usuário"
                      onClick={() => handleDeleteClick(user)}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        <line x1="10" y1="11" x2="10" y2="17"/>
                        <line x1="14" y1="11" x2="14" y2="17"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <div style={s.pagination}>
          <button
            style={pageBtn(false, currentPage === 1)}
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <ChevronLeft /> Anterior
          </button>

          {getPaginationPages(currentPage, totalPages).map((page, i) =>
            page === "..." ? (
              <span
                key={`el-${i}`}
                style={{ padding: "0 4px", color: "#aaa", fontSize: 14 }}
              >
                ...
              </span>
            ) : (
              <button
                key={page}
                style={pageBtn(currentPage === page)}
                onClick={() => setCurrentPage(page as number)}
              >
                {page}
              </button>
            ),
          )}

          <button
            style={pageBtn(false, currentPage === totalPages)}
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Próxima <ChevronRight />
          </button>
        </div>
      )}

      {/* ── Dialog: Toggle status ─────────────────────────────────────────────── */}
      <Dialog
        open={toggleDialogOpen}
        onClose={() => setToggleDialogOpen(false)}
        PaperProps={{ style: { borderRadius: 16, padding: 8, minWidth: 340 } }}
      >
        <DialogTitle
          style={{ fontWeight: 700, fontSize: 18, paddingBottom: 8 }}
        >
          {pendingUser?.isModered ? "Inativar usuário" : "Ativar usuário"}
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0 }}>
          <p style={{ fontSize: 14, color: "#555", margin: 0 }}>
            {pendingUser?.isModered
              ? "Tem certeza que deseja inativar este usuário?"
              : "Tem certeza que deseja ativar este usuário?"}
          </p>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px 20px", gap: 8 }}>
          <button
            style={s.dialogBtn}
            onClick={() => setToggleDialogOpen(false)}
          >
            Cancelar
          </button>
          <button
            style={{
              ...s.dialogBtnPrimary,
              opacity: toggleLoading ? 0.7 : 1,
              cursor: toggleLoading ? "not-allowed" : "pointer",
            }}
            disabled={toggleLoading}
            onClick={handleToggleConfirm}
          >
            {toggleLoading ? "Aguarde..." : "Confirmar"}
          </button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Excluir usuário ───────────────────────────────────────────── */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle style={{ fontWeight: 700, fontSize: 18, color: "#ec4899" }}>
          Excluir usuário
        </DialogTitle>
        <DialogContent>
          <p style={{ fontSize: 14, color: "#555", margin: 0 }}>
            Tem certeza que deseja excluir{" "}
            <strong>{deleteUser?.profile?.fullName ?? deleteUser?.email}</strong>?
            <br /><br />
            Esta ação removerá o acesso do usuário à plataforma.
          </p>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px 20px", gap: 8 }}>
          <button
            style={s.dialogBtn}
            onClick={() => setDeleteDialogOpen(false)}
          >
            Cancelar
          </button>
          <button
            style={{
              padding: "9px 22px", background: "#ec4899", border: "none",
              borderRadius: 8, color: "#fff", fontWeight: 700, fontSize: 13,
              cursor: deleteLoading ? "not-allowed" : "pointer",
              opacity: deleteLoading ? 0.7 : 1,
            }}
            disabled={deleteLoading}
            onClick={handleDeleteConfirm}
          >
            {deleteLoading ? "Excluindo..." : "Excluir"}
          </button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Análise por unidade ───────────────────────────────────────── */}
      <Dialog
        open={!!unitsUser}
        onClose={() => { setUnitsUser(null); setUnitReason(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle style={{ fontWeight: 700, fontSize: 18 }}>
          Unidades de {unitsUser?.profile?.fullName ?? unitsUser?.email}
        </DialogTitle>

        <DialogContent>
          <p style={{ fontSize: 13, color: "#888", margin: "0 0 16px", lineHeight: 1.6 }}>
            Cada unidade tem os próprios documentos. Aprovar uma não afeta as
            demais, e o acesso do usuário permanece liberado durante a análise.
          </p>

          {((unitsUser as any)?.userEstabilishments ?? []).map((ue: any) => {
            const st   = ue.documentsStatus ?? "not_sent";
            const nome = ue.estabilishment?.name ?? "Estabelecimento";
            const cfg: Record<string, { label: string; color: string; bg: string }> = {
              approved: { label: "Aprovado",   color: "#1a7a4a", bg: "#e6f7ef" },
              pending:  { label: "Em análise", color: "#b45309", bg: "#fff7e6" },
              rejected: { label: "Recusado",   color: "#b91c1c", bg: "#fee2e2" },
              not_sent: { label: "Sem documentos", color: "#888", bg: "#f5f5f5" },
            };
            const c = cfg[st] ?? cfg.not_sent;
            const emAnalise = st === "pending" || st === "rejected";

            return (
              <div
                key={ue.id}
                style={{
                  border: "1.5px solid #f0f0f0",
                  borderRadius: 10,
                  padding: "14px 16px",
                  marginBottom: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: emAnalise ? 10 : 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>{nome}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: c.color,
                    background: c.bg, borderRadius: 999, padding: "3px 10px",
                    whiteSpace: "nowrap",
                  }}>
                    {c.label}
                  </span>
                </div>

                {st === "rejected" && ue.documentsRejectionReason && (
                  <p style={{ margin: "0 0 10px", fontSize: 12, color: "#b91c1c", lineHeight: 1.5 }}>
                    {ue.documentsRejectionReason}
                  </p>
                )}

                {emAnalise && (
                  unitReason?.id === ue.id ? (
                    <div>
                      <textarea
                        autoFocus
                        rows={3}
                        value={unitReason?.text || ""}
                        onChange={(e) => setUnitReason({ id: ue.id, text: e.target.value })}
                        placeholder="Ex.: o Cartão CNPJ está ilegível."
                        style={{
                          width: "100%", padding: "9px 11px", fontSize: 13,
                          border: "1.5px solid #e8e8e8", borderRadius: 8,
                          outline: "none", resize: "vertical",
                          boxSizing: "border-box", fontFamily: "inherit",
                        }}
                      />
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <button
                          onClick={() => setUnitReason(null)}
                          style={{
                            padding: "6px 14px", borderRadius: 7,
                            border: "1.5px solid #e0e0e0", background: "#fff",
                            color: "#888", fontSize: 12, fontWeight: 600, cursor: "pointer",
                          }}
                        >
                          Voltar
                        </button>
                        <button
                          onClick={() => handleUnitReview(ue.id, "rejected", unitReason?.text)}
                          disabled={unitLoading === ue.id}
                          style={{
                            padding: "6px 14px", borderRadius: 7, border: "none",
                            background: "#ec4899", color: "#fff",
                            fontSize: 12, fontWeight: 700, cursor: "pointer",
                          }}
                        >
                          Confirmar recusa
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => handleUnitReview(ue.id, "approved")}
                        disabled={unitLoading === ue.id}
                        style={{
                          padding: "6px 14px", borderRadius: 7, border: "none",
                          background: "#00b6bc", color: "#fff",
                          fontSize: 12, fontWeight: 700, cursor: "pointer",
                          opacity: unitLoading === ue.id ? 0.6 : 1,
                        }}
                      >
                        {unitLoading === ue.id ? "Salvando..." : "Aprovar"}
                      </button>
                      <button
                        onClick={() => setUnitReason({ id: ue.id, text: "" })}
                        disabled={unitLoading === ue.id}
                        style={{
                          padding: "6px 14px", borderRadius: 7,
                          border: "1.5px solid #ec4899", background: "#fff",
                          color: "#ec4899", fontSize: 12, fontWeight: 700, cursor: "pointer",
                        }}
                      >
                        Recusar
                      </button>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px 20px" }}>
          <button style={s.dialogBtn} onClick={() => { setUnitsUser(null); setUnitReason(null); }}>
            Fechar
          </button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Análise de documentos ─────────────────────────────────────── */}
      <Dialog
        open={!!docUser}
        onClose={() => setDocUser(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle style={{
          fontWeight: 700, fontSize: 18,
          color: docAction === "approved" ? "#00b6bc" : "#ec4899",
        }}>
          {docAction === "approved" ? "Aprovar documentos" : "Recusar documentos"}
        </DialogTitle>

        <DialogContent>
          <p style={{ fontSize: 14, color: "#555", margin: "0 0 14px", lineHeight: 1.6 }}>
            {docAction === "approved" ? (
              <>
                Confirmar a aprovação dos documentos de{" "}
                <strong>{docUser?.profile?.fullName ?? docUser?.email}</strong>?
                <br /><br />
                As áreas de <strong>Banners</strong> e <strong>Notificações Push</strong>{" "}
                serão liberadas imediatamente para este usuário, caso ele opte pelo plano Connection.
              </>
            ) : (
              <>
                Informe o que precisa ser ajustado nos documentos de{" "}
                <strong>{docUser?.profile?.fullName ?? docUser?.email}</strong>.
                <br />
                O texto abaixo será enviado por e-mail.
              </>
            )}
          </p>

          {docAction === "rejected" && (
            <textarea
              value={docReason}
              onChange={(e) => setDocReason(e.target.value)}
              placeholder="Ex.: a selfie está desfocada e não é possível ler o documento."
              rows={4}
              style={{
                width: "100%", padding: "10px 12px", border: "1.5px solid #e8e8e8",
                borderRadius: 8, fontSize: 13, color: "#333", outline: "none",
                resize: "vertical", boxSizing: "border-box", fontFamily: "inherit",
              }}
            />
          )}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px 20px", gap: 8 }}>
          <button style={s.dialogBtn} onClick={() => setDocUser(null)}>
            Cancelar
          </button>
          <button
            disabled={docLoading}
            onClick={handleDocConfirm}
            style={{
              padding: "9px 22px", border: "none", borderRadius: 8,
              color: "#fff", fontWeight: 700, fontSize: 13,
              background: docAction === "approved" ? "#00b6bc" : "#ec4899",
              cursor: docLoading ? "not-allowed" : "pointer",
              opacity: docLoading ? 0.7 : 1,
            }}
          >
            {docLoading
              ? "Salvando..."
              : docAction === "approved" ? "Aprovar" : "Recusar"}
          </button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Checkout ──────────────────────────────────────────────────── */}
      <Dialog
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle style={{ fontWeight: 700, fontSize: 18 }}>
          Assinar Plano — {selectedUser?.profile?.fullName}
        </DialogTitle>
        <DialogContent
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            paddingTop: 20,
          }}
        >
          <FormControl fullWidth>
            <InputLabel>Plano</InputLabel>
            <Select
              value={selectedPlan}
              label="Plano"
              onChange={(e) => setSelectedPlan(e.target.value)}
            >
              {plans.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {`${p.name} — R$ ${parseFloat(p.price).toFixed(2)}${
                    planoPorVeiculacao(p.name) ? "" : "/mês"
                  }`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Estabelecimento</InputLabel>
            <Select
              value={selectedEstabilishment}
              label="Estabelecimento"
              onChange={(e) => setSelectedEstabilishment(e.target.value)}
            >
              {selectedUser?.customers
                ?.flatMap((c) => c.estabilishments ?? [])
                .map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px 20px", gap: 8 }}>
          <button style={s.dialogBtn} onClick={() => setCheckoutOpen(false)}>
            Cancelar
          </button>
          <button
            style={{
              ...s.dialogBtnPrimary,
              opacity: checkoutLoading ? 0.7 : 1,
              cursor: checkoutLoading ? "not-allowed" : "pointer",
            }}
            disabled={checkoutLoading}
            onClick={handleCheckout}
          >
            {checkoutLoading ? "Aguarde..." : "Ir para Checkout →"}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};